import {useRouter} from "next/router";
import {MouseEvent, ReactElement} from "react";

interface ButtonPropsInterface {
	text: string;
	transparent: boolean;
	border: boolean;
	startAdornment?: ReactElement;
	endAdornment?: ReactElement;
	href?: string;
	onClick?: (e: MouseEvent) => void;
	onHover?: (isHovering: boolean) => void;
	children?: ReactElement;
	isDropdownSubmenuButton?: boolean;
	/**
	 * pass string as tailwind class
	 * @example bgColor="bg-leo-orange"
	 */
	bgColor?: string;
	hoverBgColor?: string;
	textColor?: string;
	hoverTextColor?: string;
	borderColor?: string;
	minWidth?: string;
	textAlign?: string;
	padding?: string;
	height?: string;
	endAdornmentSpaceBetween?: boolean;
	disabled?: boolean;
	className?: string;
	type?: "button" | "submit" | "reset";
	textTransform?: string;
}

export default function Button(props: ButtonPropsInterface) {
	const router = useRouter();

	const wrapWithLink = (
		children: ReactElement,
		href?: string,
		locale?: string
	) => (
		<div
			className={`${props.isDropdownSubmenuButton ? "w-full" : ""} ${
				props.minWidth
			}`}
		>
			{children}
		</div>
	);

	function styleButton(
		border: boolean,
		transparent: boolean,
		bgColor?: string,
		hoverBgColor?: string,
		textColor?: string,
		hoverTextColor?: string,
		borderColor?: string
	) {
		var _bgColor;
		if (bgColor) {
			_bgColor = bgColor;
		} else if (transparent) {
			_bgColor = "bg-transparent";
		} else {
			_bgColor = "bg-gradient-to-b from-[#a8e1f7] to-[#43c4f7]";
		}

		var _hoverBgColor;
		if (hoverBgColor) {
			_hoverBgColor = hoverBgColor;
		} else if (transparent && !border) {
			_hoverBgColor = "hover:bg-white";
		} else {
			_hoverBgColor = "hover:bg-leo-dark-brown";
		}

		var _textcolor;
		if (textColor) {
			_textcolor = textColor;
		} else if (_bgColor === "bg-white") {
			_textcolor = "text-leo-button-gray";
		} else {
			_textcolor = "text-white";
		}

		var _hoverTextColor;
		if (hoverTextColor) {
			_hoverTextColor = hoverTextColor;
		} else if (_hoverBgColor === "hover:bg-white") {
			_hoverTextColor = "hover:text-leo-brown";
		} else {
			_hoverTextColor = "hover:text-white";
		}

		var _borderColor;
		var _border;
		if (border && borderColor) {
			_borderColor = borderColor;
			_border = "border hover:border-transparent";
		} else if (border) {
			_borderColor = "border-white";
			_border = "border hover:border-transparent";
		}
		return (
			_bgColor +
			" " +
			_hoverBgColor +
			" " +
			_textcolor +
			" " +
			_hoverTextColor +
			" " +
			_borderColor +
			" " +
			_border
		);
	}

	return wrapWithLink(
		<div
			className={`cursor-pointer relative w-full transition ${
				props.textAlign ? props.textAlign : "text-center"
			} duration-50 ${styleButton(
				props.border,
				props.transparent,
				props.bgColor,
				props.disabled ? "bg-gray-500" : props.hoverBgColor,
				props.textColor,
				props.hoverTextColor,
				props.borderColor
			)} ${props.minWidth} ${props.className ?? ""}`}
			onClick={(e) => {
				if (!props.disabled && props.onClick) {
					props.onClick(e);
				}
				if (props.href) {
					router.push(props.href);
					e.preventDefault();
					e.stopPropagation();
				}

			}}
		>
			<button
				onMouseEnter={() => {
					if (props.onHover) {
						props.onHover(true);
					}
				}}

				className={`min-h-full ${props.endAdornmentSpaceBetween && "w-full"} ${
					props.disabled && "opacity-90 cursor-not-allowed"
				}
				${props.textTransform ?? ""}
				`}
				disabled={props.disabled}
				type={props.type}
			>
				<div
					className={`flex flex-col items-start ${
						props.endAdornmentSpaceBetween ? "w-full" : "w-max"
					}`}
				>
					<div
						className={`flex gap-2 items-center py-1 ${
							props.endAdornmentSpaceBetween
								? "justify-between"
								: "justify-start"
						} ${props.padding ? props.padding : "px-5"} 
            ${props.height && props.height}
            w-full`}
					>
						{props.startAdornment && <div>{props.startAdornment}</div>}
						<div className="leading-none">{props.text}</div>
						{props.endAdornment && <div>{props.endAdornment}</div>}
					</div>
				</div>
			</button>
			{props.children}
		</div>,
		props.href
	);
}
